import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Contact, QrCode, ScanLine } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFollowWrite } from '../hooks/mutations/useFollowWrite';
import {
  useFollowersForUser,
  useFollowingForUser,
  useSearchPeopleRanked,
  useSuggestedPeople,
} from '../hooks/queries/usePeopleQueries';
import { filterPeopleByName } from '../utils/peopleSearchFilter';
import { isSearchInFlight, shouldShowSearchEmpty } from '../utils/searchUiState';
import { peopleListSurfaceClass } from '../config/glassSurfaces';
import { usePromptAuth } from './auth/AuthPrompt';
import FilterChip from './ui/FilterChip';
import Button from './ui/Button';
import SearchField from './search/SearchField';
import LineupViewToggle from './lineup/LineupViewToggle';
import Avatar from './Avatar';
import PeopleConnectionRow from './people/PeopleConnectionRow';
import PeopleListEmptyState from './people/PeopleListEmptyState';
import { useAddPeople } from './people/useAddPeople';
import './people/People.css';

/**
 * People (Stage 5, Sam's pick 2A, 2026-10-29): one page for finding people and for your own lists. Search, then the
 * three ways to add someone in person (the Events filter pills), suggestions, then Following | Followers.
 * Reached from Add friends and the Followers / Following chips on your profile (?tab=followers opens that list).
 */
export default function PeoplePage({ tab: tabProp }) {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const [params, setParams] = useSearchParams();
  const { session, authReady } = useCurrentUserProfile();
  const me = session?.user?.id ?? null;
  const enabled = authReady && Boolean(me);
  const tab =
    params.get('tab') === 'followers' || (tabProp === 'followers' && !params.get('tab'))
      ? 'followers'
      : 'following';
  const setTab = (t) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('tab', t);
        return n;
      },
      { replace: true },
    );

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);
  const searching = Boolean(debounced);

  const { follow } = useFollowWrite();
  const { openCode, startScan, sheets } = useAddPeople();
  const { data: following = [], isPending: followingPending } = useFollowingForUser(me, {
    enabled,
    limit: 1000,
  });
  const { data: followers = [], isPending: followersPending } = useFollowersForUser(me, {
    enabled: enabled && tab === 'followers',
  });
  const { data: suggested = [] } = useSuggestedPeople(me, {
    enabled: enabled && !searching,
    limit: 20,
  });
  const searchQ = useSearchPeopleRanked(me, debounced, { enabled: enabled && searching });
  const followingIds = useMemo(() => new Set(following.map((p) => p.id)), [following]);

  // Stay put (useStayPut rule): someone you follow from a card keeps their card, marked Following, until you leave
  const [keptCards, setKeptCards] = useState(() => new Map());
  const cards = useMemo(() => {
    const out = suggested.filter((p) => !followingIds.has(p.id) || keptCards.has(p.id));
    keptCards.forEach((p, id) => {
      if (!out.some((q) => q.id === id)) out.push(p);
    });
    return out;
  }, [suggested, followingIds, keptCards]);
  const followCard = (p) => {
    setKeptCards((m) => new Map(m).set(p.id, p));
    follow(p);
  };

  const results = useMemo(
    () => filterPeopleByName(searchQ.data ?? [], debounced),
    [searchQ.data, debounced],
  );
  const searchBusy = isSearchInFlight({
    input: query,
    debouncedQuery: debounced,
    isLoading: searchQ.isPending,
    isFetching: searchQ.isFetching,
  });
  const searchEmpty = shouldShowSearchEmpty({
    input: query,
    debouncedQuery: debounced,
    isLoading: searchQ.isPending,
    isFetching: searchQ.isFetching,
    resultCount: results.length,
  });

  const row = (p) => (
    <PeopleConnectionRow
      key={p.id}
      profile={p}
      isFollowing={followingIds.has(p.id)}
      onFollow={() => follow(p)}
      onNavigate={() => navigate(`/profile/${p.id}`)}
      showFollowActions={p.id !== me}
    />
  );

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="mb-6 text-dark-muted">Log in to find people.</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log In
        </Button>
      </div>
    );
  }

  const list = tab === 'followers' ? followers : following;
  const listPending = tab === 'followers' ? followersPending : followingPending;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* No title: back sits beside the search (Sam, 2026-10-30) */}
      <h1 className="sr-only">People</h1>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="!h-auto shrink-0 !p-2 -ml-2 text-dark-text"
        >
          <ChevronLeft className="h-6 w-6" aria-hidden />
        </Button>
        <div className="min-w-0 flex-1">
          <SearchField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            placeholder="Search everyone on Prii"
          />
        </div>
      </div>

      {searching ? (
        <div className="mt-4">
          {searchBusy && results.length === 0 ? null : searchEmpty ? (
            <PeopleListEmptyState>{`No people found matching "${debounced}"`}</PeopleListEmptyState>
          ) : (
            <div className={peopleListSurfaceClass()}>{results.map(row)}</div>
          )}
        </div>
      ) : (
        <>
          <div className="mt-3 flex gap-2">
            {/* My code before Scan, as on the code sheet's switch */}
            <FilterChip pill className="flex-1 !justify-center gap-1.5" onClick={openCode}>
              <QrCode className="h-4 w-4 shrink-0" aria-hidden />
              My code
            </FilterChip>
            <FilterChip pill className="flex-1 !justify-center gap-1.5" onClick={startScan}>
              <ScanLine className="h-4 w-4 shrink-0" aria-hidden />
              Scan
            </FilterChip>
            <FilterChip
              pill
              className="flex-1 !justify-center gap-1.5"
              onClick={() => navigate('/people/contacts')}
            >
              <Contact className="h-4 w-4 shrink-0" aria-hidden />
              Contacts
            </FilterChip>
          </div>

          {cards.length > 0 && (
            <>
              <p className="pp-label mt-5">Suggested</p>
              <div className="pp-cards">
                {cards.map((p) => {
                  const on = followingIds.has(p.id);
                  return (
                    <div
                      key={p.id}
                      className="pp-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/profile/${p.id}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/profile/${p.id}`)}
                    >
                      <Avatar
                        displayName={p.display_name}
                        profilePhotoUrl={p.profile_photo_url}
                        size="md"
                      />
                      <b className="mt-2 block w-full truncate text-sm text-dark-text">
                        {p.display_name}
                      </b>
                      <Button
                        variant={on ? 'secondary' : 'primary'}
                        size="sm"
                        fullWidth
                        className="mt-2"
                        disabled={on}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!on) followCard(p);
                        }}
                      >
                        {on ? 'Following' : 'Follow'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="mt-5">
            <LineupViewToggle
              view={tab}
              onChange={setTab}
              options={[
                { value: 'following', label: 'Following' },
                { value: 'followers', label: 'Followers' },
              ]}
              ariaLabel="Your people"
              className="!mx-0 !mb-2"
            />
            {listPending && list.length === 0 ? null : list.length === 0 ? (
              <PeopleListEmptyState>
                {tab === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
              </PeopleListEmptyState>
            ) : (
              <div className={peopleListSurfaceClass()}>{list.map(row)}</div>
            )}
          </div>
        </>
      )}
      {sheets}
    </div>
  );
}
